import type { FC } from "react";
import {
  LinearProgress,
  linearProgressClasses,
  Paper,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import type { ModifiedPollResponse, PollResponseWithTotal } from "@/framework/KioForm/fields/PollResponseResultsField";
import Styles from "@/assets/styles/Styles";
import { useTranslation } from "react-i18next";

const BorderLinearProgress = styled(LinearProgress)(({ theme }) => ({
  height: 10,
  borderRadius: 5,
  [`&.${linearProgressClasses.colorPrimary}`]: {
    backgroundColor: Styles.Colors.HEADER_SEPARATOR_COLOR,
  },
  [`& .${linearProgressClasses.bar}`]: {
    borderRadius: 5,
    backgroundColor: Styles.Colors.THEME_PRIMARY,
  },
}));

const Container = styled("div")``;

interface PollResponseResultsTableProps {
  pollResults?: ModifiedPollResponse;
}

interface PollResponseResultsTableRowProps extends PollResponseResultsTableProps {
  pollDataKey: string;
  pollDataValue: number;
  hideBarGraph?: boolean;
}

const PollResponseResultsTableRow: FC<PollResponseResultsTableRowProps> = ({
  pollDataKey,
  pollDataValue,
  pollResults,
  hideBarGraph = false,
}) => {
  return (
    <TableRow key={pollDataKey} sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
      <TableCell component="th" scope="row">
        {pollDataKey}
      </TableCell>
      <TableCell align="right">{pollDataValue}</TableCell>
      <TableCell align="right">
        {pollResults?.total ? ((pollDataValue / pollResults.total) * 100).toFixed(1) + "%" : "0%"}
      </TableCell>
      <TableCell align="right">
        {!hideBarGraph && (
          <BorderLinearProgress
            variant="determinate"
            value={pollDataValue > 0 && !!pollResults?.total ? (pollDataValue / pollResults.total) * 100 : 0}
          />
        )}
      </TableCell>
    </TableRow>
  );
};

const PollResponseResultsTable: FC<PollResponseResultsTableProps> = ({ pollResults }) => {
  const pollData = Object.entries(pollResults?.poll_data ?? {});
  const { t } = useTranslation("common");
  return (
    <TableContainer sx={{ marginBottom: "32px" }} component={Paper}>
      <Table sx={{ minWidth: 400 }}>
        <TableHead>
          <TableRow>
            <TableCell>{t("polls.answerAlternative")}</TableCell>
            <TableCell align="right">{t("polls.count")}</TableCell>
            <TableCell align="right">{t("polls.percentage")}</TableCell>
            <TableCell align="left" sx={{ width: "30%" }}></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {pollData
            .toSorted((el1, el2) => {
              return el2[1] - el1[1];
            })
            .map(([pollDataKey, PollDataValue]) => (
              <PollResponseResultsTableRow
                key={pollDataKey}
                pollDataValue={PollDataValue}
                pollDataKey={pollDataKey}
                pollResults={pollResults}
              />
            ))}
          <PollResponseResultsTableRow
            pollDataValue={pollResults?.total ?? 0}
            pollDataKey={t("polls.total")}
            pollResults={pollResults}
            hideBarGraph={true}
          />
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default PollResponseResultsTable;
